import React, { useContext, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Snackbar, ProjectorShimmer } from 'Components';
import { AppContext } from 'Context';
import { AxiosCancelToken, dataFetcher, endpoints } from 'Api';
import { ReactComponent as AddPersonIcon } from 'Assets/add-person.svg';
import Search from '@mui/icons-material/Search';
import { Pathname } from 'Routes';

import {
  NameEditModal,
  EmailEditModal,
  PhoneNumberEditModal,
  PasswordEditModal
} from 'Components/edits';

import { ViewerPage } from '../components/page';
import { DataView } from '../components/data-view';
import { Input } from '../../../public/authentication/components/input-field';
import UserRequest from './UserRequest';
import styles from './index.module.css';

const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

export const Viewers = () => {
  // eslint-disable-next-line no-script-url
  window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const { langText, isViewerListUpdated, setViewerListUpdate } = useContext(AppContext);
  const location = useLocation();
  const [accessRequests, setAccessRequests] = useState([]);
  const [hasAccess, setHasAccess] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [pendingViewersList, setPendingViewersList] = useState([]);
  const [searchKey, setSearchKey] = useState(undefined);
  const [openGrantAccessModal, setGrantAccessModal] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [filterType, setFilterType] = useState('all'); // 'all', 'active', 'pending'
  const searchInputRef = useRef(null);
  const viewerText = langText?.viewersSideBar?.viewers;
  const commonText = langText?.common;
  const { isValidating: fetchingViewers, mutate: fetchViewerList } = useSWR([
    endpoints.getViewersList, searchKey], {
    fetcher: (url, search, status) => dataFetcher(url, { search, status }),
    onSuccess: ({ success, data }) => {
      if (success && data) {
        setViewers(data);
      } else {
        setViewers([]);
      }
    },
    onError: () => {
      setViewers([]);
    },
  });

  const { isValidating: fetchingPendingList, mutate: fetchPendingList } = useSWR([
    endpoints.getPendingViewersList], {
    fetcher: (url, search, status) => dataFetcher(url, { search, status }),
    onSuccess: ({ success, data }) => {
      if (success && data) {
        setPendingViewersList(data);
      } else {
        setPendingViewersList([]);
      }
    },
    onError: () => {
      setPendingViewersList([]);
    },
  });

  useEffect(() => {
    fetchViewerList();
  }, [searchKey]);

  useEffect(() => {
    fetchPendingList();
  }, []);

  useEffect(() => {
    if (openGrantAccessModal && !location.search?.includes(Pathname.grantAccess)) {
      fetchPendingList();
    }
  }, [openGrantAccessModal, location.search]);

  useEffect(() => {
    if (location.search?.includes(Pathname.grantAccess)) {
      setGrantAccessModal(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (isViewerListUpdated) {
      fetchViewerList();
      fetchPendingList();
      setViewerListUpdate(false);
    }
  }, [isViewerListUpdated]);

  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  const handleSearchIconClick = () => {
    setIsSearchExpanded(true);
  };

  const handleSearchBlur = () => {
    if (!searchKey) {
      setIsSearchExpanded(false);
    }
  };

  const handleSearchChange = (e) => {
    e.persist();
    if (!e?.target?.value) {
      setSearchKey(undefined);
    } else {
      setSearchKey(e?.target?.value);
    }
  };

  const searchElement = (
    <div className={styles.searchContainer}>
      <button
        type="button"
        className={`${styles.searchIconWrapper} ${isSearchExpanded ? styles.searchIconHidden : ''}`}
        onClick={handleSearchIconClick}
        aria-label="Open search"
      >
        <Search className={styles.searchIcon} />
      </button>
      <input
        ref={searchInputRef}
        id="SearchInput"
        className={`${styles.viewerSearchinput} ${isSearchExpanded ? styles.searchExpanded : ''}`}
        onChange={handleSearchChange}
        onBlur={handleSearchBlur}
        placeholder={viewerText?.searchViewer || 'Search Viewers'}
        type="text"
        value={searchKey || ''}
      />
    </div>
  );

  return (
    <>
      <ViewerPage
        title={'Viewers' || viewerText?.title}
        addActionName={viewerText?.addViewer || 'Add viewer'}
        link={Pathname.grantAccess}
        searchElement={filterType === 'active' ? searchElement : null}
      // subTitle={'you can manage viewers here' || viewerText?.subTitle}
      >
        <div className={styles.viewersDiv}>
          <div className={styles.allViewersContainer}>
            {/* Filter Section */}
            <div className={styles.filterSection}>
              <button
                className={`${styles.filterButton} ${filterType === 'all' ? styles.filterButtonActive : ''}`}
                onClick={() => setFilterType('all')}
              >
                All
              </button>
              <button
                className={`${styles.filterButton} ${filterType === 'active' ? styles.filterButtonActive : ''}`}
                onClick={() => setFilterType('active')}
              >
                Active
              </button>
              <button
                className={`${styles.filterButton} ${filterType === 'pending' ? styles.filterButtonActive : ''}`}
                onClick={() => setFilterType('pending')}
              >
                Pending
              </button>
            </div>

            <div className={styles.viewerList}>
              <div className={styles.memberHeaderRow}>
                <span className={styles.memberHeaderName}>
                  Name
                </span>
                <span className={styles.memberHeaderEmail}>
                  Email
                </span>
                <span className={styles.memberHeaderActions}>
                  Actions
                </span>
              </div>

              {/* Active Viewers */}
              {(filterType === 'all' || filterType === 'active') && viewers?.map(({ id, firstname, lastname, email, image }, idx) => (
                <UserRequest
                  email={email}
                  id={id}
                  key={idx}
                  firstname={firstname}
                  lastname={lastname}
                  image={image}
                  setSnackbar={setSnackbar}
                  hasAccess
                  fetchAccessRequests={fetchViewerList}
                  setAccessRequests={setViewers}
                  setHasAccess={setViewers}
                />
              ))}

              {/* Pending Viewers */}
              {(filterType === 'all' || filterType === 'pending') && pendingViewersList?.map(({ id, firstname, lastname, email, type, image }, idx) => (
                <UserRequest
                  email={email}
                  id={id}
                  key={idx}
                  firstname={firstname}
                  lastname={lastname}
                  image={image}
                  setSnackbar={setSnackbar}
                  fetchAccessRequests={fetchPendingList}
                  setAccessRequests={setPendingViewersList}
                  setHasAccess={setViewers}
                  type={type}
                  fetchViewerList={fetchViewerList}
                />
              ))}
            </div>
          </div>
          {/* <div className={styles.pendingViewersContainer}>
            <div className={styles.viewerSubheader}>
              <h3 className={styles.subsectionTitle}>Pending Viewers</h3>

            </div>

            <div className={styles.viewerList}>
              <div className={styles.memberHeaderRow}>
                <span className={styles.memberHeaderName}>
                  Name
                </span>
                <span className={styles.memberHeaderEmail}>
                  Email
                </span>
                <span className={styles.memberHeaderActions}>
                  Actions
                </span>
              </div>

              {pendingViewersList?.map(({ id, firstname, lastname, email, type, image }, idx) => (
                <UserRequest
                  email={email}
                  id={id}
                  key={idx}
                  firstname={firstname}
                  lastname={lastname}
                  image={image}
                  setSnackbar={setSnackbar}
                  fetchAccessRequests={fetchPendingList}
                  setAccessRequests={setPendingViewersList}
                  setHasAccess={setViewers}
                  type={type}
                  fetchViewerList={fetchViewerList}
                />
              ))}
            </div>
          </div> */}
        </div>
        {/* {
          fetchingViewers || fetchingPendingList ? (
            <ProjectorShimmer isLight />
          ) : (
            <div className={styles.viewersDiv}>
              <div className={styles.allViewersContainer}>
                <input
                  id="SearchInput"
                  className={styles.viewerSearchinput}
                  onChange={(e) => {
                    e.persist();
                    if (!e?.target?.value) {
                      setSearchKey(undefined);
                    } else {
                      setSearchKey(e?.target?.value);
                    }
                  }}
                  placeholder={viewerText?.searchViewer || 'Search Viewers'}
                  type="text"
                />
                <div className={styles.viewerList}>
                  {viewers?.map(({ id, firstname, lastname, email, image }, idx) => (
                    <UserRequest
                      email={email}
                      id={id}
                      key={idx}
                      firstname={firstname}
                      lastname={lastname}
                      image={image}
                      setSnackbar={setSnackbar}
                      hasAccess
                      fetchAccessRequests={fetchViewerList}
                      setAccessRequests={setViewers}
                      setHasAccess={setViewers}
                    />
                  ))}
                </div>
              </div>
              <div className={styles.pendingViewersContainer}>
                <div className={styles.pendingTitle}>{viewerText?.pendingViewers || 'Pending Viewers'}</div>
                <div className={styles.viewerList}>
                  {pendingViewersList?.map(({ id, firstname, lastname, email, type, image }, idx) => (
                    <UserRequest
                      email={email}
                      id={id}
                      key={idx}
                      firstname={firstname}
                      lastname={lastname}
                      image={image}
                      setSnackbar={setSnackbar}
                      fetchAccessRequests={fetchPendingList}
                      setAccessRequests={setPendingViewersList}
                      setHasAccess={setViewers}
                      type={type}
                      fetchViewerList={fetchViewerList}
                    />
                  ))}
                </div>
              </div>
            </div>
          )
        } */}
      </ViewerPage>

      <Snackbar
        isVisible={snackbar?.isVisible}
        message={snackbar?.message || ''}
        onClose={() => { setSnackbar(defaultSnackbar); }}
        type={snackbar?.type}
      />
    </>
  );
};
